import { Terminal } from '@xterm/xterm';
import { Drive, drives } from '../fs';
import { PathParser } from '../util/PathParser';

export const createTerm = async () => {
    // Create a fullscreen container for the terminal
    let termContainer = document.getElementById('xterm-fullscreen-container');
    if (!termContainer) {
        termContainer = document.createElement('div');
        termContainer.id = 'xterm-fullscreen-container';
        Object.assign(termContainer.style, {
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100vw',
            height: '100vh',
            background: 'black',
            zIndex: '9999',
            display: 'flex',
            flexDirection: 'column',
        });
        document.body.appendChild(termContainer);
    }

    // Create the xterm instance
    const term = new Terminal({
        cursorBlink: true,
        theme: { background: '#000000' },
        fontFamily: 'monospace',
        fontSize: 18,
        cols: 80,
        rows: 24,
    });
    term.open(termContainer);
    term.focus();
    term.write('Welcome to the Supernova installer shell.\r\nYou can type Platform Utility commands here.');

    // Optional: Add a close button
    let closeBtn = document.getElementById('xterm-close-btn');
    if (!closeBtn) {
        closeBtn = document.createElement('button');
        closeBtn.id = 'xterm-close-btn';
        closeBtn.textContent = 'Close Terminal';
        Object.assign(closeBtn.style, {
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: '10000',
            padding: '8px 16px',
            background: '#222',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
        });
        closeBtn.onclick = () => {
            term.dispose();
            termContainer.remove();
        };
        termContainer.appendChild(closeBtn);
    }

    // --- Terminal prompt, command system, and keyboard logic ---
    let command = '';
    let cursorPosition = 0;
    let currentDir = 'C:/Pluto/System41';

    function getPrompt() {
        return `${currentDir} $ `;
    }

    function prompt() {
        command = '';
        cursorPosition = 0;
        term.write('\r\n' + getPrompt());
    }

    // Command system
    const commands: Record<string, { f: (args: string[]) => void | Promise<void>, description: string }> = {
        cd: {
            f: async (args: string[]) => {
                let newPath = '';
                if (args.length === 0 || args[0] === '' || args[0] === '~') {
                    newPath = '/';
                } else if (args[0] === '..') {
                    if (currentDir !== '/') {
                        const parts = currentDir.split('/').filter(Boolean);
                        parts.pop();
                        newPath = '/' + parts.join('/');
                        if (newPath === '') newPath = '/';
                    } else {
                        newPath = '/';
                    }
                } else if (args[0].startsWith('/')) {
                    newPath = args[0].replace(/\/+/g, '/');
                } else {
                    if (currentDir === '/') {
                        newPath = '/' + args[0];
                    } else {
                        newPath = currentDir + '/' + args[0];
                    }
                    newPath = newPath.replace(/\/+/g, '/');
                }

                // Try to resolve and check if directory exists
                let [driveLetter, relPath] = PathParser(newPath.startsWith('/') ? 'C:' + newPath : newPath);
                let drive = drives.find(d => d.driveLetter.toUpperCase() === driveLetter.toUpperCase());
                if (!drive || !drive.driverInstance) {
                    term.writeln(`\r\nNo such drive: ${driveLetter}`);
                    return;
                }
                try {
                    const files = await drive.driverInstance.readDir(relPath === '' ? '\\' : relPath);
                    if (Array.isArray(files)) {
                        currentDir = newPath;
                    } else {
                        term.writeln(`\r\nNot a directory: ${newPath}`);
                    }
                } catch (e) {
                    term.writeln(`\r\nNo such directory: ${newPath}`);
                }
            },
            description: 'Change the current directory (verifies with fs)',
        },
        ls: {
            f: async (args: string[]) => {
                let pathToList = currentDir;
                if (args.length > 0) {
                    if (args[0].startsWith('/')) {
                        pathToList = args[0];
                    } else if (currentDir === '/') {
                        pathToList = '/' + args[0];
                    } else {
                        pathToList = currentDir + '/' + args[0];
                    }
                }
                let [driveLetter, relPath] = PathParser(pathToList.startsWith('/') ? 'C:' + pathToList : pathToList);
                let drive = drives.find(d => d.driveLetter.toUpperCase() === driveLetter.toUpperCase());
                if (!drive || !drive.driverInstance) {
                    term.writeln(`\r\nNo such drive: ${driveLetter}`);
                    return;
                }
                try {
                    const files = await drive.driverInstance.readDir(relPath === '' ? '\\' : relPath);
                    if (Array.isArray(files)) {
                        if (files.length === 0) {
                            term.writeln('\r\n<empty>');
                        } else {
                            term.writeln('\r\n' + files.map(f => f.replace(/^\\/, '')).join('    '));
                        }
                    } else {
                        term.writeln(`\r\nNot a directory: ${pathToList}`);
                    }
                } catch (e) {
                    term.writeln(`\r\nNo such directory: ${pathToList}`);
                }
            },
            description: 'List files in the current directory',
        },
    };

    async function runCommand(text: string) {
        const [cmd, ...args] = text.trim().split(/\s+/);
        if (cmd.length > 0) {
            if (commands[cmd]) {
                await commands[cmd].f(args);
            } else {
                term.writeln(`\r\n${cmd}: command not found`);
            }
        }
        prompt();
    }

    // Print prompt after initial log
    prompt();

    term.onData((e) => {
        switch (e) {
            case '\u0003': // Ctrl+C
                term.write('^C');
                prompt();
                break;
            case '\r': // Enter
                runCommand(command);
                break;
            case '\u007F': // Backspace (DEL)
                if (cursorPosition > 0) {
                    command = command.slice(0, cursorPosition - 1) + command.slice(cursorPosition);
                    cursorPosition--;
                    term.write('\b \b');
                    if (cursorPosition < command.length) {
                        const restOfLine = command.slice(cursorPosition);
                        const moveBack = '\u001b[' + restOfLine.length + 'D';
                        term.write(restOfLine + ' ' + moveBack);
                    }
                }
                break;
            case '\u001b[C': // Right arrow
                if (cursorPosition < command.length) {
                    term.write(e);
                    cursorPosition++;
                }
                break;
            case '\u001b[D': // Left arrow
                if (cursorPosition > 0) {
                    term.write(e);
                    cursorPosition--;
                }
                break;
            default:
                if ((e >= String.fromCharCode(0x20) && e <= String.fromCharCode(0x7E)) || e >= '\u00a0') {
                    command = command.slice(0, cursorPosition) + e + command.slice(cursorPosition);
                    cursorPosition++;
                    term.write(e);
                    if (cursorPosition < command.length) {
                        const restOfLine = command.slice(cursorPosition);
                        const moveBack = '\u001b[' + restOfLine.length + 'D';
                        term.write(restOfLine + moveBack);
                    }
                }
        }
    });

    return term;
}